// Design Ref: §2.1 — Application 레이어. 모든 auth side-effect의 단일 진입점
// 책임: bootstrap(init+마이그레이션+profile), 소셜 콜백 후처리, logout, refresh 큐, 보호 fetch
// 순환 의존 회피: refresh는 fetch 직접 호출(api.js 거치지 않음). authStore는 method body에서만 참조

import { browser } from '$app/environment';
import { API_BASE_URL } from '$lib/config/api.js';
import { authStore } from '$lib/stores/authStore.js';
import { createRefreshQueue } from '$lib/auth/refreshQueue.js';

const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

// Design §3.1 — 모듈 스코프 상태
let bootstrapPromise = null;
let migrated = false;
// 동시 다발 401 시 logout이 N번 호출되며 백엔드 logout / posthog.reset 폭주하는 것 방지
let logoutPromise = null;

// Plan FR-03, FR-08 — 단일 refresh + 대기 큐. 백엔드는 쿠키의 refresh를 읽어 rotation
export const refresh = createRefreshQueue(async () => {
	const response = await fetch(`${API_BASE_URL}/api/auth/token/refresh/`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' }
	});
	return response.ok;
});

// Plan FR-05 — 세션당 1회 이행. 성공/실패 무관하게 localStorage 정리
async function migrateFromStorageIfNeeded() {
	if (migrated || !browser) {
		migrated = true;
		return;
	}
	migrated = true;
	const access = localStorage.getItem(ACCESS_KEY);
	const refreshToken = localStorage.getItem(REFRESH_KEY);
	if (!access || !refreshToken) return;
	try {
		await fetch(`${API_BASE_URL}/api/auth/token/migrate-from-storage/`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ access, refresh: refreshToken })
		});
	} catch {
		// 네트워크 실패도 동일 처리: 잔존 localStorage는 제거
	}
	localStorage.removeItem(ACCESS_KEY);
	localStorage.removeItem(REFRESH_KEY);
}

async function fetchProfile() {
	let response = await fetch(`${API_BASE_URL}/api/auth/profile/`, {
		credentials: 'include'
	});
	if (response.status === 401) {
		const ok = await refresh();
		if (!ok) return null;
		response = await fetch(`${API_BASE_URL}/api/auth/profile/`, {
			credentials: 'include'
		});
	}
	if (!response.ok) return null;
	const result = await response.json().catch(() => null);
	return result?.data || result || null;
}

// Plan FR-05, FR-09 — init 단일 진입점. bootstrapPromise 가드로 동시 호출 안전
export async function bootstrap() {
	if (!browser) return;
	if (bootstrapPromise) return bootstrapPromise;
	bootstrapPromise = (async () => {
		try {
			await migrateFromStorageIfNeeded();
			const user = await fetchProfile();
			if (user) {
				// 기존 동작 유지: 이름/전화번호 없으면 강제 로그아웃
				if (!user.name || !user.phone) {
					await logout({ redirect: true });
					return;
				}
				authStore.login(user);
				authStore.markInitialized();
			} else {
				authStore.setUnauthenticated();
			}
		} catch {
			authStore.setUnauthenticated();
		}
	})();
	return bootstrapPromise;
}

// Plan FR-04 — 백엔드 logout 호출 + localStorage 안전망 + 상태 리셋 + (옵션) 리다이렉트
// 동시 다발 401로 인한 중복 호출은 in-flight Promise 공유로 합쳐 백엔드/redirect 폭주 방지
export async function logout({ redirect = false } = {}) {
	if (logoutPromise) return logoutPromise;
	logoutPromise = (async () => {
		if (browser) {
			try {
				await fetch(`${API_BASE_URL}/api/auth/logout/`, {
					method: 'POST',
					credentials: 'include'
				});
			} catch {
				// 백엔드 호출 실패해도 클라이언트 상태는 정리
			}
			localStorage.removeItem(ACCESS_KEY);
			localStorage.removeItem(REFRESH_KEY);
		}
		authStore.setUnauthenticated();
		if (redirect && browser) {
			window.location.href = '/auth/login';
		}
	})();
	try {
		await logoutPromise;
	} finally {
		// 동일 사이클의 대기자 모두 같은 결과를 받은 뒤 해제
		logoutPromise = null;
	}
}

// Plan FR-06 — 소셜 콜백 응답에서 user만 추출. body의 tokens는 무시(백엔드가 이미 쿠키 발급)
export function handleSocialCallback(result) {
	const user = result?.data?.user || result?.user || null;
	authStore.login(user);
	authStore.markInitialized();
}

// Plan FR-07 — 보호 파일 다운로드용. blob 응답이라 apiRequest와 분리
export async function protectedFetch(url) {
	let response = await fetch(url, { credentials: 'include' });
	if (response.status === 401) {
		const ok = await refresh();
		if (ok) {
			response = await fetch(url, { credentials: 'include' });
		}
	}
	return response;
}
